import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBranding } from '../hooks/useBranding';
import Footer from '../components/Footer';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const { login, completeMfa } = useAuth();
  const { ttcLogo } = useBranding();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({ google: false, microsoft: false });

  const [notice, setNotice] = useState('');

  useEffect(() => {
    api.get('/auth/providers').then(r => setProviders(r.data)).catch(() => {});
    // Arrived here because the session idled out — say so rather than looking
    // like a random sign-out.
    if (new URLSearchParams(window.location.search).get('timeout') === '1') {
      setNotice('You were signed out after a period of inactivity. Please sign in again.');
      window.history.replaceState({}, '', '/login');
    }
  }, []);
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [stage, setStage] = useState('login');   // 'login' | 'mfa'
  const [enroll, setEnroll] = useState(null);     // { qr, secret } when first-time setup
  const [maskedEmail, setMaskedEmail] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotMsg('');
    try {
      const r = await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotMsg(r.data.message || 'If that email is registered, a reset link has been sent.');
    } catch (_) {
      setForgotMsg('If that email is registered, a reset link has been sent.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r.ok) { nav('/'); return; }
      // MFA required
      setMaskedEmail(r.email || '');
      setEnroll(r.mfa_setup_required ? { qr: r.qr, secret: r.secret } : null);
      setStage('mfa');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await completeMfa(mfaCode);
      if (r.ok) { nav('/'); return; }
      setError('Invalid or expired code.');
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailCode = async () => {
    setError('');
    try {
      const r = await api.post('/auth/mfa/email-code');
      setEmailSent(true);
      setMaskedEmail(r.data.email || maskedEmail);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send a code.');
    }
  };

  const msClientId = window._env_?.MICROSOFT_CLIENT_ID;
  const googleClientId = window._env_?.GOOGLE_CLIENT_ID;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          {ttcLogo && <img className="login-logo-img" src={ttcLogo} alt="Toledo Ticket" />}
          <h1>Toledo Ticket OMS</h1>
          <p>Order Management System</p>
          <div className="gold-bar" />
        </div>

        {stage === 'login' && (providers.microsoft || providers.google) && (
        <div className="sso-buttons">
          {providers.microsoft && (
          <a href="/auth/microsoft" className="sso-btn">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
              <rect x="13" y="1" width="10" height="10" fill="#7fba00"/>
              <rect x="1" y="13" width="10" height="10" fill="#00a4ef"/>
              <rect x="13" y="13" width="10" height="10" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </a>
          )}
          {providers.google && (
          <a href="/auth/google" className="sso-btn">
            <svg viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </a>
          )}
        </div>
        )}

        {stage === 'login' && (providers.microsoft || providers.google) && (
        <div className="divider">or sign in with email</div>
        )}

        {notice && <div className="alert alert-info">{notice}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {stage === 'login' && (<>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@example.com"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>
          <button type="submit" className="btn btn-primary btn-lg" style={{width:'100%'}} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        {!forgot ? (
          <button type="button" className="link-btn" style={{ marginTop: 14 }} onClick={() => { setForgot(true); setForgotMsg(''); }}>
            Forgot password?
          </button>
        ) : (
          <div style={{ marginTop: 14 }}>
            {forgotMsg ? (
              <p className="form-hint" style={{ textAlign: 'center' }}>{forgotMsg}</p>
            ) : (
              <form onSubmit={submitForgot}>
                <div className="form-group">
                  <label className="form-label">Enter your email to reset your password</label>
                  <input type="email" className="form-input" required value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)} placeholder="you@company.com" />
                </div>
                <button type="submit" className="btn btn-secondary" style={{ width: '100%' }}>Send reset link</button>
              </form>
            )}
            <button type="button" className="link-btn" style={{ marginTop: 10 }} onClick={() => setForgot(false)}>
              Back to sign in
            </button>
          </div>
        )}
        </>)}

        {stage === 'mfa' && (
          <div>
            {enroll ? (
              <>
                <h3 style={{ marginTop: 0 }}>Set up two-factor authentication</h3>
                <p className="form-hint">Scan this QR code with an authenticator app (Google Authenticator, Authy, Microsoft Authenticator), then enter the 6-digit code it shows.</p>
                {enroll.qr && <div style={{ textAlign: 'center', margin: '12px 0' }}><img src={enroll.qr} alt="QR code" style={{ width: 180, height: 180 }} /></div>}
                {enroll.secret && <p className="form-hint" style={{ textAlign: 'center' }}>Or enter this key manually: <code>{enroll.secret}</code></p>}
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Enter your verification code</h3>
                <p className="form-hint">Open your authenticator app and enter the 6-digit code, or email yourself a code instead.</p>
              </>
            )}
            <form onSubmit={submitMfa}>
              <div className="form-group">
                <label className="form-label">6-digit code</label>
                <input className="form-input" value={mfaCode} onChange={e => setMfaCode(e.target.value)}
                  inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456" />
              </div>
              <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Verifying…' : 'Verify'}
              </button>
            </form>
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              {emailSent
                ? <p className="form-hint">A code was sent to {maskedEmail}. Enter it above.</p>
                : <button type="button" className="link-btn" onClick={sendEmailCode}>Email me a code instead</button>}
            </div>
            <button type="button" className="link-btn" style={{ marginTop: 10 }}
              onClick={() => { setStage('login'); setMfaCode(''); setEnroll(null); setEmailSent(false); setError(''); }}>
              Back to sign in
            </button>
          </div>
        )}
      </div>
      <div className="login-footer"><Footer /></div>
    </div>
  );
}
